import { useEffect, useRef, useState } from 'react'
import { Button } from '../ui/button'
import { GoogleIcon, MetaMaskIcon } from './icons'
import { useAccount, useConnect, useSignMessage } from 'wagmi'
import { useAuthModal } from '@/context/AuthModalContext'
import { useLoader } from '@/context/LoaderContext'
import { loadGoogleIdentityScript } from '@/lib/google-identity'
import { clearReferralCode, getReferralCode } from '@/lib/referral-tracking'
import { showApiErrorToast } from '@/lib/api-toast'
import { useGetMetaMaskNonceMutation, useGoogleLoginMutation, useMetaMaskLoginMutation } from '@/store/api/authApi'

export function SocialLoginButtons() {
	const [googleReady, setGoogleReady] = useState(false)
	const requestInFlightRef = useRef(false)
	const codeClientRef = useRef<{ requestCode: () => void } | null>(null)
	const { close, requireUsername } = useAuthModal()
	const { showLoader, hideLoader } = useLoader()
	const { address, connector: activeConnector, isConnected } = useAccount()
	const { connectAsync, connectors, isPending: isConnecting } = useConnect()
	const { signMessageAsync, isPending: isSigning } = useSignMessage()
	const [googleLogin, { isLoading: isGoogleLoading }] = useGoogleLoginMutation()
	const [getMetaMaskNonce, { isLoading: isNonceLoading }] = useGetMetaMaskNonceMutation()
	const [metaMaskLogin, { isLoading: isMetaMaskLoading }] = useMetaMaskLoginMutation()
	const [isMetaMaskBusy, setIsMetaMaskBusy] = useState(false)
	const [isAwaitingWalletConnection, setIsAwaitingWalletConnection] = useState(false)
	const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID?.trim()
	const isLoading = !googleReady || isGoogleLoading || isConnecting || isNonceLoading || isSigning || isMetaMaskLoading || isMetaMaskBusy
	const isLoaderVisible = !googleReady || isGoogleLoading || isNonceLoading || isSigning || isMetaMaskLoading || (isMetaMaskBusy && !isAwaitingWalletConnection)

	useEffect(() => {
		if (!isLoaderVisible) {
			return
		}

		showLoader()
		return () => hideLoader()
	}, [hideLoader, isLoaderVisible, showLoader])

	useEffect(() => {
		if (!clientId) {
			setGoogleReady(true)
			showApiErrorToast('Google sign-in is not configured.')
			return
		}

		let cancelled = false

		void loadGoogleIdentityScript()
			.then(() => {
				if (cancelled) {
					return
				}

				if (!window.google?.accounts.oauth2) {
					throw new Error('Google Identity Services failed to initialize.')
				}

				codeClientRef.current = window.google.accounts.oauth2.initCodeClient({
					client_id: clientId,
					scope: 'openid email profile',
					ux_mode: 'popup',
					select_account: true,
					callback: ({ code, error, error_description }) => {
						if (error || !code) {
							requestInFlightRef.current = false
							showApiErrorToast(error_description || error || 'Google authorization did not return a code.')
							return
						}

						const referrerCode = getReferralCode()
						void googleLogin({
							code,
							redirect_uri: window.location.origin,
							...(referrerCode ? { referrer_code: referrerCode } : {}),
						})
							.unwrap()
							.then((response) => {
								if (response.status_code === 201) {
									clearReferralCode()
								}

								if (response.data.has_username === false) {
									requireUsername()
								} else {
									close()
								}
							})
							.catch(() => {
								// The API interceptor displays the authentication error.
							})
							.finally(() => {
								requestInFlightRef.current = false
							})
					},
					error_callback: ({ type }) => {
						requestInFlightRef.current = false
						if (type !== 'popup_closed') {
							showApiErrorToast(type === 'popup_failed_to_open' ? 'Unable to open the Google sign-in popup.' : 'Google sign-in failed to start.')
						}
					},
				})

				setGoogleReady(true)
			})
			.catch((error: unknown) => {
				if (!cancelled) {
					setGoogleReady(true)
					showApiErrorToast(error instanceof Error ? error.message : 'Unable to initialize Google sign-in.')
				}
			})

		return () => {
			cancelled = true
			codeClientRef.current = null
		}
	}, [clientId, close, googleLogin, requireUsername])

	const signInWithMetaMask = async () => {
		if (requestInFlightRef.current) {
			return
		}

		requestInFlightRef.current = true
		setIsMetaMaskBusy(true)

		try {
			let walletAddress = address
			const isMetaMaskConnector = activeConnector?.id === 'metaMask' || activeConnector?.name.toLowerCase().includes('metamask')

			if (!isConnected || !walletAddress || !isMetaMaskConnector) {
				const metaMaskConnector = connectors.find((item) => item.id === 'metaMask' || item.name.toLowerCase().includes('metamask'))
				if (!metaMaskConnector) {
					showApiErrorToast('MetaMask is not available.')
					return
				}

				setIsAwaitingWalletConnection(true)
				try {
					const connection = await connectAsync({ connector: metaMaskConnector })
					walletAddress = connection.accounts[0]
				} catch (error) {
					showApiErrorToast(error instanceof Error ? error.message : 'Unable to connect to MetaMask.')
					return
				} finally {
					setIsAwaitingWalletConnection(false)
				}
			}

			if (!walletAddress) {
				showApiErrorToast('Unable to get the connected MetaMask account.')
				return
			}

			let nonce: string
			try {
				const response = await getMetaMaskNonce(walletAddress).unwrap()
				nonce = response.data.nonce
				if (!nonce) {
					showApiErrorToast('The server did not return a MetaMask sign-in nonce.')
					return
				}
			} catch {
				return
			}

			const message = `Signing into BetsCycle using the one-time nonce ${nonce}. This request was issued at ${new Date().toISOString()}.`

			let signature: `0x${string}`
			try {
				signature = await signMessageAsync({ account: walletAddress, message })
			} catch (error) {
				showApiErrorToast(error instanceof Error ? error.message : 'Unable to sign the MetaMask authentication message.')
				return
			}

			try {
				const referrerCode = getReferralCode()
				const response = await metaMaskLogin({
					address: walletAddress,
					message,
					signature,
					...(referrerCode ? { referrer_code: referrerCode } : {}),
				}).unwrap()
				if (response.status_code === 201) {
					clearReferralCode()
				}

				if (response.data.has_username === false) {
					requireUsername()
				} else {
					close()
				}
			} catch {
				// The API interceptor displays the authentication error.
			}
		} catch (error) {
			showApiErrorToast(error instanceof Error ? error.message : 'MetaMask sign-in failed.')
		} finally {
			requestInFlightRef.current = false
			setIsMetaMaskBusy(false)
		}
	}

	const signInWithGoogle = () => {
		if (!codeClientRef.current || requestInFlightRef.current) {
			return
		}

		requestInFlightRef.current = true
		try {
			codeClientRef.current.requestCode()
		} catch {
			requestInFlightRef.current = false
			showApiErrorToast('Unable to start Google sign-in.')
		}
	}

	return (
		<div className='mt-3 grid grid-cols-2 gap-1'>
			<Button className='min-w-0' variant='secondary' type='button' disabled={!codeClientRef.current || isLoading} onClick={() => void signInWithGoogle()}>
				<GoogleIcon />
				Google
			</Button>
			<Button className='min-w-0' variant='secondary' type='button' disabled={isLoading} onClick={() => void signInWithMetaMask()}>
				<MetaMaskIcon />
				MetaMask
			</Button>
		</div>
	)
}
